import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  CaretRight,
  ChatText,
  Cpu,
  Database,
  Envelope,
  FileText,
  Globe,
  Image,
  Lightning,
  Microphone,
  PaperPlaneRight,
  WebhooksLogo
} from '@phosphor-icons/react';

/**
 * MiniFlowDiagram - Compact visual representation of agent flow
 * 
 * Shows a simplified horizontal flow:
 * [Inputs] → [Capabilities] → [Outputs]
 * 
 * Updates in real-time as agent config changes
 */
const MiniFlowDiagram = ({ agentConfig }) => {
  const inputIcons = {
    text: ChatText,
    audio: Microphone,
    file_pdf: FileText,
    file_docx: FileText,
    file_csv: Database,
    file_xlsx: Database,
    webhook: WebhooksLogo,
    api_payload: WebhooksLogo,
    url: Globe,
    image: Image,
    email: Envelope
  };

  // Process config into flow data
  const flowData = useMemo(() => {
    const inputs = (agentConfig.accepted_inputs || ['text']).slice(0, 3);
    const capabilities = [
      ...(agentConfig.required_capabilities || []),
      ...(agentConfig.optional_capabilities || [])
    ].slice(0, 4);
    const outputs = (agentConfig.expected_outputs || []).slice(0, 2);

    return { inputs, capabilities, outputs };
  }, [agentConfig]);

  const formatLabel = (str) => {
    if (typeof str !== 'string') return str?.label || 'Output';
    return str.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ').substring(0, 12);
  };

  // Check if we have meaningful content to show
  const hasContent = flowData.inputs.length > 0 || 
                     flowData.capabilities.length > 0 || 
                     flowData.outputs.length > 0;

  if (!hasContent) {
    return (
      <div className="flex items-center justify-center p-6 text-center">
        <div className="text-slate-500 text-sm">
          Configure your agent to see the flow diagram
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      {/* Flow Diagram */}
      <div className="flex items-center justify-between gap-2 overflow-x-auto pb-2">
        {/* Inputs Column */}
        <div className="flex-shrink-0 min-w-[100px]">
          <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-2 text-center">
            Inputs
          </div>
          <div className="space-y-1.5">
            {flowData.inputs.map((input, idx) => {
              const Icon = inputIcons[input] || ChatText;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  className="flex items-center gap-1.5 px-2 py-1.5 rounded-md 
                    bg-blue-500/10 border border-blue-500/20"
                >
                  <Icon className="w-3 h-3 text-blue-400 flex-shrink-0" />
                  <span className="text-[10px] text-blue-300 truncate">
                    {formatLabel(input)}
                  </span>
                </motion.div>
              );
            })}
            {flowData.inputs.length === 0 && (
              <div className="px-2 py-1.5 rounded-md bg-slate-500/10 border border-slate-500/20">
                <span className="text-[10px] text-slate-500">No inputs</span>
              </div>
            )}
          </div>
        </div>

        {/* Arrow */}
        <div className="flex-shrink-0 flex items-center px-1">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="w-6 h-[2px] bg-gradient-to-r from-blue-500 to-purple-500"
          />
          <CaretRight className="w-3 h-3 text-purple-400 -ml-1" />
        </div>

        {/* Capabilities Column */}
        <div className="flex-shrink-0 min-w-[120px]">
          <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-2 text-center">
            Capabilities
          </div>
          <div className="space-y-1.5">
            {flowData.capabilities.length > 0 ? (
              flowData.capabilities.map((cap, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + idx * 0.05 }}
                  className="flex items-center gap-1.5 px-2 py-1.5 rounded-md 
                    bg-purple-500/10 border border-purple-500/20"
                >
                  <Cpu className="w-3 h-3 text-purple-400 flex-shrink-0" />
                  <span className="text-[10px] text-purple-300 truncate">
                    {formatLabel(cap)}
                  </span>
                </motion.div>
              ))
            ) : (
              <div className="px-2 py-1.5 rounded-md bg-purple-500/5 border border-purple-500/10 border-dashed">
                <span className="text-[10px] text-purple-400/50">Add capabilities</span>
              </div>
            )}
            {flowData.capabilities.length > 4 && (
              <div className="text-[10px] text-purple-400 text-center">
                +{flowData.capabilities.length - 4} more
              </div>
            )}
          </div>
        </div>

        {/* Arrow */}
        <div className="flex-shrink-0 flex items-center px-1">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2 }}
            className="w-6 h-[2px] bg-gradient-to-r from-purple-500 to-emerald-500"
          />
          <CaretRight className="w-3 h-3 text-emerald-400 -ml-1" />
        </div>

        {/* Outputs Column */}
        <div className="flex-shrink-0 min-w-[100px]">
          <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-2 text-center">
            Outputs
          </div>
          <div className="space-y-1.5">
            {flowData.outputs.length > 0 ? (
              flowData.outputs.map((output, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 + idx * 0.05 }}
                  className="flex items-center gap-1.5 px-2 py-1.5 rounded-md 
                    bg-emerald-500/10 border border-emerald-500/20"
                >
                  <PaperPlaneRight className="w-3 h-3 text-emerald-400 flex-shrink-0" />
                  <span className="text-[10px] text-emerald-300 truncate">
                    {formatLabel(output)}
                  </span>
                </motion.div>
              ))
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="flex items-center gap-1.5 px-2 py-1.5 rounded-md 
                  bg-emerald-500/10 border border-emerald-500/20"
              >
                <PaperPlaneRight className="w-3 h-3 text-emerald-400 flex-shrink-0" />
                <span className="text-[10px] text-emerald-300">Response</span>
              </motion.div>
            )}
          </div>
        </div>
      </div>

      {/* Flow Stats */}
      <div className="flex items-center justify-center gap-4 mt-3 pt-3 border-t border-white/5">
        <FlowStat 
          label="Inputs" 
          value={flowData.inputs.length} 
          color="text-blue-400" 
        />
        <FlowStat 
          label="Capabilities" 
          value={(agentConfig.required_capabilities?.length || 0) + (agentConfig.optional_capabilities?.length || 0)} 
          color="text-purple-400" 
        />
        <FlowStat 
          label="Outputs" 
          value={Math.max(flowData.outputs.length, 1)} 
          color="text-emerald-400" 
        />
      </div>
    </div>
  );
};

// Small stat component
const FlowStat = ({ label, value, color }) => (
  <div className="text-center">
    <div className={`text-lg font-bold ${color}`}>{value}</div>
    <div className="text-[10px] text-slate-500 uppercase tracking-wider">{label}</div>
  </div>
);

export default MiniFlowDiagram;
